/**
 * BIL-09 — how the printed invoice is laid out on paper.
 *
 * The same model as the prescription layout (LAB-04): paper, margins, a header
 * and footer that can be the clinic letterhead, an uploaded image, space kept
 * for pre-printed paper, or nothing — then which columns print and where each
 * element sits. The paper, band and position vocabulary is the prescription's
 * own (`RX_*`), so both screens and both sheets speak one language. Stored as
 * one clinic-wide setting (`print.invoice`); the API validates with
 * `validateInvoicePrintLayout` and every reader goes through
 * `normaliseInvoicePrintLayout`.
 */

import {
  RX_ELEMENT_ALIGNS,
  RX_ELEMENT_ANCHORS,
  RX_FOOTER_MODES,
  RX_HEADER_MODES,
  RX_LIMITS,
  RX_ORIENTATIONS,
  RX_PAPER_MM,
  RX_PAPER_SIZES,
  type RxFooterMode,
  type RxHeaderMode,
  type RxOrientation,
  type RxPaperSize,
  type RxPrintBand,
  type RxPrintElement,
} from './prescription-print';

/** BOTH prints the Arabic description with the English one under it. */
export const INVOICE_ITEM_NAME_LANGUAGES = ['AR', 'EN', 'BOTH'] as const;
export type InvoiceItemNameLanguage = (typeof INVOICE_ITEM_NAME_LANGUAGES)[number];

export const INVOICE_PRINT_COLUMNS = [
  'index',
  'item',
  'provider',
  'quantity',
  'unitPrice',
  'discount',
  'net',
] as const;
export type InvoicePrintColumn = (typeof INVOICE_PRINT_COLUMNS)[number];

export const INVOICE_PRINT_ELEMENTS = [
  'clinicHeader',
  'documentTitle',
  'invoiceNumber',
  'date',
  'patientName',
  'fileNumber',
  'phone',
  'insurance',
  'totals',
  'einvoice',
  'thanks',
  'signature',
] as const;
export type InvoicePrintElementKey = (typeof INVOICE_PRINT_ELEMENTS)[number];

/**
 * BIL-10 — an accepted invoice must carry its JoFotara QR, so this element can
 * be moved but never switched off.
 */
export const INVOICE_REQUIRED_ELEMENTS: readonly InvoicePrintElementKey[] = ['einvoice'];

export type InvoicePrintElement = Omit<RxPrintElement, 'key'> & { key: InvoicePrintElementKey };

/** The clinic's own wording for the thank-you note; an empty one falls back to the other language. */
export const INVOICE_THANKS_MAX_LENGTH = 200;
export const DEFAULT_INVOICE_THANKS_TEXT = { ar: 'شكراً لزيارتكم', en: 'Thank you for your visit' };

export interface InvoicePrintLayout {
  paperSize: RxPaperSize;
  orientation: RxOrientation;
  marginsMm: { top: number; bottom: number; start: number; end: number };
  header: RxPrintBand<RxHeaderMode>;
  footer: RxPrintBand<RxFooterMode>;
  fontScalePercent: number;
  itemNameLanguage: InvoiceItemNameLanguage;
  thanksText: { ar: string; en: string };
  /** Print order; hidden columns keep their place for when they are switched back on. */
  columns: { key: InvoicePrintColumn; visible: boolean }[];
  table: { xMm: number; yMm: number; widthMm: number; showHeaderRow: boolean };
  /** Settings order and paint order; hidden elements keep their place. */
  elements: InvoicePrintElement[];
}

/** Placed in the letterhead's opposite corner by auto-arrange. */
const CORNER_HEIGHT_MM: Partial<Record<InvoicePrintElementKey, number>> = {
  documentTitle: 9,
  invoiceNumber: 10,
  date: 10,
  insurance: 10,
};

/** Reproduces the sheet as it printed before the layout became configurable. */
export const DEFAULT_INVOICE_PRINT_LAYOUT: InvoicePrintLayout = autoArrangeInvoiceElements({
  paperSize: 'A4',
  orientation: 'portrait',
  marginsMm: { top: 12, bottom: 12, start: 12, end: 12 },
  header: { mode: 'SYSTEM', imageFileId: null, heightMm: 35 },
  footer: { mode: 'NONE', imageFileId: null, heightMm: 20 },
  fontScalePercent: 100,
  itemNameLanguage: 'AR',
  thanksText: { ...DEFAULT_INVOICE_THANKS_TEXT },
  columns: INVOICE_PRINT_COLUMNS.map((key) => ({ key, visible: true })),
  table: { xMm: 0, yMm: 0, widthMm: 186, showHeaderRow: true },
  elements: INVOICE_PRINT_ELEMENTS.map((key) => ({
    key,
    visible: true,
    showLabel: true,
    anchor: 'PAGE',
    xMm: 0,
    yMm: 0,
    widthMm: 186,
    align: 'start',
  })),
});

/** Arrange the current list without changing visibility, labels or its order. */
export function autoArrangeInvoiceElements(layout: InvoicePrintLayout): InvoicePrintLayout {
  const paper = RX_PAPER_MM[layout.paperSize];
  const width = Math.max(
    5,
    (layout.orientation === 'landscape' ? paper.height : paper.width) -
      layout.marginsMm.start -
      layout.marginsMm.end,
  );
  const slots = Math.max(1, Math.min(3, Math.floor(width / 40)));
  const gap = slots > 1 ? 4 : 0;
  const shortWidth = (width - gap * (slots - 1)) / slots;
  // The usual letterhead has the title, number and date in the opposite corner.
  const leadingHeader =
    layout.elements[0]?.key === 'clinicHeader' &&
    layout.elements[0].visible &&
    layout.header.mode === 'SYSTEM';
  const cornerWidth = Math.min(60, width / 3);
  const cornerHeight = layout.elements.reduce(
    (sum, element) => sum + (element.visible ? (CORNER_HEIGHT_MM[element.key] ?? 0) : 0),
    0,
  );
  const totalsWidth = Math.min(85, (width - 4) / 2);
  const closingWidth = (width - 4) / 2;

  let y = 0;
  let slot = 0;
  let cornerY = 0;
  let closingSlot = 0;
  const elements = layout.elements.map((element): InvoicePrintElement => {
    const next: InvoicePrintElement = {
      ...element,
      anchor: 'PAGE',
      xMm: 0,
      yMm: y,
      widthMm: width,
      align: 'start',
    };
    switch (element.key) {
      case 'totals':
        return { ...next, anchor: 'AFTER_TABLE', xMm: width - totalsWidth, yMm: 4, widthMm: totalsWidth };
      case 'einvoice':
        return { ...next, anchor: 'AFTER_TABLE', yMm: 4, widthMm: Math.max(5, width - totalsWidth - 4) };
      case 'thanks':
      case 'signature': {
        const placed: InvoicePrintElement = {
          ...next,
          anchor: 'AFTER_TABLE',
          yMm: 60,
          widthMm: closingWidth,
          xMm: closingSlot * (closingWidth + 4),
          align: closingSlot ? 'end' : 'start',
        };
        if (element.visible) closingSlot = (closingSlot + 1) % 2;
        return placed;
      }
      case 'clinicHeader':
        if (slot) {
          y += 13;
          slot = 0;
        }
        next.yMm = y;
        if (leadingHeader) next.widthMm = width - cornerWidth - 2;
        if (element.visible && layout.header.mode === 'SYSTEM') {
          y += leadingHeader ? Math.max(28, cornerHeight + 2) : 28;
        }
        return next;
    }
    const cornerStep = CORNER_HEIGHT_MM[element.key];
    if (leadingHeader && cornerStep) {
      next.widthMm = cornerWidth;
      next.xMm = width - cornerWidth;
      next.yMm = cornerY;
      next.align = 'end';
      if (element.visible) cornerY += cornerStep;
      return next;
    }
    next.widthMm = shortWidth;
    next.xMm = slot * (shortWidth + gap);
    next.yMm = y;
    if (element.visible && ++slot === slots) {
      slot = 0;
      y += 13;
    }
    return next;
  });
  if (slot) y += 13;
  return { ...layout, elements, table: { ...layout.table, xMm: 0, yMm: y + 3, widthMm: width } };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const pick = <T extends string>(allowed: readonly T[], value: unknown, fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback;

const clamp = (value: unknown, range: { min: number; max: number }, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.min(range.max, Math.max(range.min, value))
    : fallback;

function normaliseBand<Mode extends string>(
  value: unknown,
  modes: readonly Mode[],
  fallback: RxPrintBand<Mode>,
): RxPrintBand<Mode> {
  const band = isRecord(value) ? value : {};
  return {
    mode: pick(modes, band.mode, fallback.mode),
    imageFileId:
      typeof band.imageFileId === 'string' && band.imageFileId.trim() ? band.imageFileId.trim() : null,
    heightMm: clamp(band.heightMm, RX_LIMITS.bandMm, fallback.heightMm),
  };
}

/** Any stored shape → a complete layout; unknown or out-of-range parts fall back to the default. */
export function normaliseInvoicePrintLayout(value: unknown): InvoicePrintLayout {
  const d = DEFAULT_INVOICE_PRINT_LAYOUT;
  const raw = isRecord(value) ? value : {};
  const margins = isRecord(raw.marginsMm) ? raw.marginsMm : {};
  const thanks = isRecord(raw.thanksText) ? raw.thanksText : {};
  const text = (value: unknown, fallback: string) =>
    typeof value === 'string' ? value.slice(0, INVOICE_THANKS_MAX_LENGTH) : fallback;

  const seen = new Set<InvoicePrintColumn>();
  const columns: InvoicePrintLayout['columns'] = [];
  for (const entry of Array.isArray(raw.columns) ? raw.columns : []) {
    if (!isRecord(entry)) continue;
    const key = entry.key as InvoicePrintColumn;
    if (!INVOICE_PRINT_COLUMNS.includes(key) || seen.has(key)) continue;
    seen.add(key);
    columns.push({ key, visible: entry.visible !== false });
  }
  // A column added after the clinic saved its layout joins at the end, as the default has it.
  for (const column of d.columns) if (!seen.has(column.key)) columns.push({ ...column });

  const base: InvoicePrintLayout = {
    paperSize: pick(RX_PAPER_SIZES, raw.paperSize, d.paperSize),
    orientation: pick(RX_ORIENTATIONS, raw.orientation, d.orientation),
    marginsMm: {
      top: clamp(margins.top, RX_LIMITS.marginMm, d.marginsMm.top),
      bottom: clamp(margins.bottom, RX_LIMITS.marginMm, d.marginsMm.bottom),
      start: clamp(margins.start, RX_LIMITS.marginMm, d.marginsMm.start),
      end: clamp(margins.end, RX_LIMITS.marginMm, d.marginsMm.end),
    },
    header: normaliseBand(raw.header, RX_HEADER_MODES, d.header),
    footer: normaliseBand(raw.footer, RX_FOOTER_MODES, d.footer),
    fontScalePercent: clamp(raw.fontScalePercent, RX_LIMITS.fontScalePercent, d.fontScalePercent),
    itemNameLanguage: pick(INVOICE_ITEM_NAME_LANGUAGES, raw.itemNameLanguage, d.itemNameLanguage),
    thanksText: { ar: text(thanks.ar, d.thanksText.ar), en: text(thanks.en, d.thanksText.en) },
    columns,
    elements: d.elements.map((element) => ({ ...element })),
    table: { ...d.table },
  };
  const arranged = autoArrangeInvoiceElements(base);
  const seenElements = new Set<InvoicePrintElementKey>();
  const elements: InvoicePrintElement[] = [];
  for (const entry of Array.isArray(raw.elements) ? raw.elements : []) {
    if (!isRecord(entry)) continue;
    const key = entry.key as InvoicePrintElementKey;
    if (!INVOICE_PRINT_ELEMENTS.includes(key) || seenElements.has(key)) continue;
    seenElements.add(key);
    const fallback = arranged.elements.find((element) => element.key === key)!;
    elements.push({
      key,
      visible:
        INVOICE_REQUIRED_ELEMENTS.includes(key) ||
        (typeof entry.visible === 'boolean' ? entry.visible : fallback.visible),
      showLabel: typeof entry.showLabel === 'boolean' ? entry.showLabel : true,
      anchor: pick(RX_ELEMENT_ANCHORS, entry.anchor, fallback.anchor),
      align: pick(RX_ELEMENT_ALIGNS, entry.align, fallback.align),
      xMm: clamp(entry.xMm, RX_LIMITS.coordinateMm, fallback.xMm),
      yMm: clamp(entry.yMm, RX_LIMITS.coordinateMm, fallback.yMm),
      widthMm: clamp(entry.widthMm, RX_LIMITS.widthMm, fallback.widthMm),
    });
  }
  for (const element of arranged.elements) {
    if (!seenElements.has(element.key)) elements.push({ ...element });
  }
  const table = isRecord(raw.table) ? raw.table : {};
  return {
    ...base,
    elements,
    table: {
      xMm: clamp(table.xMm, RX_LIMITS.coordinateMm, arranged.table.xMm),
      yMm: clamp(table.yMm, RX_LIMITS.coordinateMm, arranged.table.yMm),
      widthMm: clamp(table.widthMm, RX_LIMITS.widthMm, arranged.table.widthMm),
      showHeaderRow: typeof table.showHeaderRow === 'boolean' ? table.showHeaderRow : true,
    },
  };
}

/**
 * Strict check for a write: the normaliser forgives a stored value, but a save
 * that would be silently rewritten is refused instead. Returns the offending
 * path, or null when the value is a complete, valid layout.
 */
export function validateInvoicePrintLayout(value: unknown): string | null {
  if (!isRecord(value)) return 'layout';
  if (!RX_PAPER_SIZES.includes(value.paperSize as RxPaperSize)) return 'paperSize';
  if (!RX_ORIENTATIONS.includes(value.orientation as RxOrientation)) return 'orientation';
  if (!INVOICE_ITEM_NAME_LANGUAGES.includes(value.itemNameLanguage as InvoiceItemNameLanguage)) {
    return 'itemNameLanguage';
  }

  const inRange = (n: unknown, range: { min: number; max: number }) =>
    typeof n === 'number' && Number.isFinite(n) && n >= range.min && n <= range.max;

  if (!inRange(value.fontScalePercent, RX_LIMITS.fontScalePercent)) return 'fontScalePercent';
  if (!isRecord(value.thanksText)) return 'thanksText';
  for (const lang of ['ar', 'en'] as const) {
    const wording = value.thanksText[lang];
    if (typeof wording !== 'string' || wording.length > INVOICE_THANKS_MAX_LENGTH) {
      return `thanksText.${lang}`;
    }
  }
  if (!isRecord(value.marginsMm)) return 'marginsMm';
  for (const side of ['top', 'bottom', 'start', 'end'] as const) {
    if (!inRange(value.marginsMm[side], RX_LIMITS.marginMm)) return `marginsMm.${side}`;
  }

  const bands = [
    ['header', RX_HEADER_MODES],
    ['footer', RX_FOOTER_MODES],
  ] as const;
  for (const [name, modes] of bands) {
    const band = value[name];
    if (!isRecord(band)) return name;
    if (!(modes as readonly string[]).includes(band.mode as string)) return `${name}.mode`;
    if (!inRange(band.heightMm, RX_LIMITS.bandMm)) return `${name}.heightMm`;
    if (
      band.imageFileId !== null &&
      (typeof band.imageFileId !== 'string' || band.imageFileId.length > 64)
    ) {
      return `${name}.imageFileId`;
    }
    if (band.mode === 'IMAGE' && !band.imageFileId) return `${name}.imageFileId`;
  }

  if (!Array.isArray(value.columns) || value.columns.length !== INVOICE_PRINT_COLUMNS.length) {
    return 'columns';
  }
  const keys = new Set<string>();
  for (const column of value.columns) {
    if (!isRecord(column) || typeof column.visible !== 'boolean') return 'columns';
    if (
      !INVOICE_PRINT_COLUMNS.includes(column.key as InvoicePrintColumn) ||
      keys.has(column.key as string)
    ) {
      return 'columns';
    }
    keys.add(column.key as string);
  }

  if (!isRecord(value.table)) return 'table';
  for (const axis of ['xMm', 'yMm', 'widthMm'] as const) {
    if (
      !inRange(value.table[axis], axis === 'widthMm' ? RX_LIMITS.widthMm : RX_LIMITS.coordinateMm)
    ) {
      return `table.${axis}`;
    }
  }
  if (typeof value.table.showHeaderRow !== 'boolean') return 'table.showHeaderRow';
  if (!Array.isArray(value.elements) || value.elements.length !== INVOICE_PRINT_ELEMENTS.length) {
    return 'elements';
  }
  const elementKeys = new Set<string>();
  for (const [index, element] of value.elements.entries()) {
    const path = `elements.${index}`;
    if (!isRecord(element)) return path;
    const key = element.key as InvoicePrintElementKey;
    if (!INVOICE_PRINT_ELEMENTS.includes(key) || elementKeys.has(key)) return `${path}.key`;
    elementKeys.add(key);
    for (const flag of ['visible', 'showLabel'] as const) {
      if (typeof element[flag] !== 'boolean') return `${path}.${flag}`;
    }
    if (INVOICE_REQUIRED_ELEMENTS.includes(key) && !element.visible) return `${path}.visible`;
    if (!RX_ELEMENT_ANCHORS.includes(element.anchor as InvoicePrintElement['anchor'])) {
      return `${path}.anchor`;
    }
    if (!RX_ELEMENT_ALIGNS.includes(element.align as InvoicePrintElement['align'])) {
      return `${path}.align`;
    }
    for (const axis of ['xMm', 'yMm', 'widthMm'] as const) {
      if (
        !inRange(element[axis], axis === 'widthMm' ? RX_LIMITS.widthMm : RX_LIMITS.coordinateMm)
      ) {
        return `${path}.${axis}`;
      }
    }
  }
  return null;
}
